/**
 * Rede (cadastro + importação) → dados da cena 3D — Nexora Grid (frontend, US-405; nasceu na prova S3).
 *
 * Criado em: 16/10/2026 · Última modificação: 18/10/2026 · Versão: 1.3.0
 *
 * Lê o `.inp` e o `.geojson` gerados pelo importador de DWG da S1 (dado de cliente, servido só localmente) e
 * converte longitude/latitude em metros locais (x = leste, z = sul). Setor de cada trecho vem do código da
 * camada (RSE, RAP, VRP, BON); casas por nó vêm do comentário `;N` das junções.
 * v1.3 (18/10/2026): bomba submersa e dados do poço (NE, ND, crivo, filtros, fundo) vindos da ficha técnica.
 */

export type No = { id: string; lon: number; lat: number; cota: number; casas: number; x: number; z: number };
export type Trecho = { id: string; de: string; para: string; dn: number; comprimento: number; setor: string; rugosidade?: number };
/** Poço da bomba submersa (NBR 12212): profundidades a partir da boca, em metros. */
export type DadosPoco = Partial<Record<"profundidade_poco_m" | "nivel_estatico_m" | "nivel_dinamico_m" | "profundidade_instalacao_m" | "topo_filtros_m", number>>;
export type Equipamento = { tipo: string; id: string; detalhe: string; lon: number; lat: number; x: number; z: number; setor: string; trecho: string;
  ativoId?: string; deviceId?: string | null; situacao?: string; submersa?: boolean; poco?: DadosPoco | null;
  reservatorio?: { altura_lamina_m?: number | null; nivel_max_m?: number | null; volume_m3?: number | null; tipo_construtivo?: string } | null };
export type Rede = {
  nos: Map<string, No>; trechos: Trecho[]; equipamentos: Equipamento[]; origem: { lon: number; lat: number };
  cotaMin: number; cotaMax: number; extensao: number;
  divisas?: { type: "FeatureCollection"; features: { type: "Feature"; geometry: { type: "LineString"; coordinates: number[][] }; properties: Record<string, unknown> }[] };
};

const M_POR_GRAU_LAT = 110540;

/** Família e código do setor a partir do resto do nome da camada (".001RSE", "RP-003", ".006RSE.B"...). */
export function setorDoCodigo(codigo: string): string {
  const limpo = codigo.replace(/^\./, "").toUpperCase();
  const familia = ["RSE", "RAP", "VRP", "BON", "REL"].find((f) => limpo.includes(f))
    ?? ({ SE: "RSE", AP: "RAP", RP: "VRP", ON: "BON" } as Record<string, string>)[limpo.slice(0, 2)];
  const numero = limpo.match(/\d+/)?.[0]?.replace(/^0+/, "") ?? "";
  return familia ? `${familia}${numero ? "-" + numero.padStart(3, "0") : ""}` : "OUTROS";
}

export function lerRede(textoInp: string, geojson: { features: { geometry: { coordinates: number[] }; properties: Record<string, string> }[] }): Rede {
  const nos = new Map<string, No>();
  const trechos: Trecho[] = [];
  const coords = new Map<string, [number, number]>();
  const cotas = new Map<string, { cota: number; casas: number }>();
  let secao = "";
  for (const bruta of textoInp.split(/\r?\n/)) {
    const [corpo, comentario = ""] = bruta.split(";");
    const linha = corpo.trim();
    if (linha.startsWith("[")) { secao = linha.toUpperCase(); continue; }
    if (!linha) continue;
    const p = linha.split(/\s+/);
    if (secao === "[JUNCTIONS]") cotas.set(p[0], { cota: Number(p[1]), casas: Number(comentario.trim()) || 0 });
    if (secao === "[RESERVOIRS]") cotas.set(p[0], { cota: Number(p[1]) - 6, casas: 0 });
    if (secao === "[COORDINATES]") coords.set(p[0], [Number(p[1]), Number(p[2])]);
    if (secao === "[PIPES]" && !p[0].startsWith("L_")) {
      const partes = comentario.trim().split(/\s+/);
      trechos.push({ id: p[0], de: p[1], para: p[2], comprimento: Number(p[3]), dn: Number(p[4]),
        setor: setorDoCodigo(partes[partes.length - 1] ?? "") });
    }
  }
  const lons = [...coords.values()].map((c) => c[0]);
  const lats = [...coords.values()].map((c) => c[1]);
  const origem = { lon: (Math.min(...lons) + Math.max(...lons)) / 2, lat: (Math.min(...lats) + Math.max(...lats)) / 2 };
  const mPorGrauLon = M_POR_GRAU_LAT * Math.cos((origem.lat * Math.PI) / 180);
  const projetar = (lon: number, lat: number) => ({ x: (lon - origem.lon) * mPorGrauLon, z: -(lat - origem.lat) * M_POR_GRAU_LAT });
  coords.forEach(([lon, lat], id) => {
    const c = cotas.get(id);
    if (c) nos.set(id, { id, lon, lat, cota: c.cota, casas: c.casas, ...projetar(lon, lat) });
  });
  const equipamentos = geojson.features.map((f) => {
    const [lon, lat] = f.geometry.coordinates;
    return { tipo: f.properties.tipo, id: f.properties.id, detalhe: f.properties.detalhe ?? "", lon, lat, setor: "", trecho: "",
      ...projetar(lon, lat) };
  });
  atribuirSetores(nos, trechos, equipamentos);
  afastarEstacoes(equipamentos);
  const valores = [...nos.values()].map((n) => n.cota);
  const xs = [...nos.values()].map((n) => n.x);
  const zs = [...nos.values()].map((n) => n.z);
  return { nos, trechos, equipamentos, origem, cotaMin: Math.min(...valores), cotaMax: Math.max(...valores),
    extensao: Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs)) };
}

/**
 * Setor e trecho de cada equipamento = os do trecho mais próximo (meio do trecho, busca em grade de 200 m).
 * O próprio código do reservatório (RSE-001, RAP-002) costuma nomear o setor que ele abastece.
 */
function atribuirSetores(nos: Map<string, No>, trechos: Trecho[], equipamentos: Equipamento[]) {
  const LADO = 200;
  const grade = new Map<string, { x: number; z: number; setor: string; id: string }[]>();
  for (const t of trechos) {
    const a = nos.get(t.de);
    const b = nos.get(t.para);
    if (!a || !b) continue;
    const m = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2, setor: t.setor, id: t.id };
    const chave = `${Math.floor(m.x / LADO)}:${Math.floor(m.z / LADO)}`;
    grade.set(chave, [...(grade.get(chave) ?? []), m]);
  }
  for (const e of equipamentos) {
    const gx = Math.floor(e.x / LADO), gz = Math.floor(e.z / LADO);
    let melhor = Infinity;
    for (let dx = -2; dx <= 2; dx++) for (let dz = -2; dz <= 2; dz++) {
      for (const m of grade.get(`${gx + dx}:${gz + dz}`) ?? []) {
        const d = (m.x - e.x) ** 2 + (m.z - e.z) ** 2;
        if (d < melhor) { melhor = d; e.setor = m.setor; e.trecho = m.id; }
      }
    }
    if (!e.setor) e.setor = "OUTROS";
  }
}

const ESTACOES = new Set(["bomba", "poco_profundo", "elevatoria"]);

/**
 * O símbolo do DWG põe a estação colada no reservatório; com o reservatório em escala real no 3D (1.500 m³ ≈ 13 m
 * de diâmetro) ela ficaria dentro dele. Afasta a estação para fora da borda. Só muda x/z do 3D: lon/lat (mapa e
 * cadastro) continuam os do DWG.
 */
function afastarEstacoes(equipamentos: Equipamento[]) {
  const reservatorios = equipamentos.filter((e) => e.tipo === "reservatorio");
  for (const e of equipamentos.filter((q) => ESTACOES.has(q.tipo))) {
    for (const r of reservatorios) {
      const volume = Number(r.detalhe.match(/\d+/)?.[0] ?? 0) || 100;
      const raio = 1.6 * Math.cbrt(volume / (r.id.startsWith("REL") ? 4 : 17)) + 4;
      const dx = e.x - r.x, dz = e.z - r.z;
      const d = Math.hypot(dx, dz);
      if (d >= raio) continue;
      const ux = d > 0.01 ? dx / d : 1, uz = d > 0.01 ? dz / d : 0;
      e.x = r.x + ux * raio;
      e.z = r.z + uz * raio;
    }
  }
}

/** Nós de cada setor (pelos trechos). */
export function nosPorSetor(rede: Rede): Map<string, Set<string>> {
  const mapa = new Map<string, Set<string>>();
  for (const t of rede.trechos) {
    const s = mapa.get(t.setor) ?? new Set<string>();
    s.add(t.de); s.add(t.para);
    mapa.set(t.setor, s);
  }
  return mapa;
}

/** Casas agrupadas por célula (padrão 300 m): centro ponderado e total de ligações. */
export function agruparCasas(rede: Rede, lado = 300) {
  const celulas = new Map<string, { x: number; z: number; casas: number }>();
  rede.nos.forEach((n) => {
    if (!n.casas) return;
    const chave = `${Math.floor(n.x / lado)}:${Math.floor(n.z / lado)}`;
    const c = celulas.get(chave) ?? { x: 0, z: 0, casas: 0 };
    c.x += n.x * n.casas; c.z += n.z * n.casas; c.casas += n.casas;
    celulas.set(chave, c);
  });
  return [...celulas.values()].map((c) => ({ x: c.x / c.casas, z: c.z / c.casas, casas: c.casas }));
}

/** Projeta lon/lat → metros locais da rede (para focar a câmera vindo do mapa). */
export function projetar(rede: Rede, lon: number, lat: number) {
  const mPorGrauLon = M_POR_GRAU_LAT * Math.cos((rede.origem.lat * Math.PI) / 180);
  return { x: (lon - rede.origem.lon) * mPorGrauLon, z: -(lat - rede.origem.lat) * M_POR_GRAU_LAT };
}

// ------------------------------------------------------------------------------
// US-405: rede a partir da API (importação vigente + cadastro de ativos)
// ------------------------------------------------------------------------------

export type NoApi = { id: string; longitude: number; latitude: number; casas: number };
export type TrechoApi = { id: string; de: string; para: string; comprimento_m: number; dn_mm: number; rugosidade: number; setor: string };
export type FeicaoAtivo = { geometry: { coordinates: number[] }; properties: Record<string, unknown> };

/** Monta a rede com cota 0 (as cotas vêm do relevo depois, em `aplicarCotas`). */
export function redeDaApi(nosApi: NoApi[], trechosApi: TrechoApi[], feicoes: FeicaoAtivo[]): Rede {
  const lons = nosApi.map((n) => n.longitude), lats = nosApi.map((n) => n.latitude);
  const origem = { lon: (Math.min(...lons) + Math.max(...lons)) / 2, lat: (Math.min(...lats) + Math.max(...lats)) / 2 };
  const mPorGrauLon = M_POR_GRAU_LAT * Math.cos((origem.lat * Math.PI) / 180);
  const projetarLocal = (lon: number, lat: number) => ({ x: (lon - origem.lon) * mPorGrauLon, z: -(lat - origem.lat) * M_POR_GRAU_LAT });
  const nos = new Map<string, No>();
  nosApi.forEach((n) => nos.set(n.id, { id: n.id, lon: n.longitude, lat: n.latitude, cota: 0, casas: n.casas, ...projetarLocal(n.longitude, n.latitude) }));
  const trechos: Trecho[] = trechosApi.map((t) => ({ id: t.id, de: t.de, para: t.para, dn: t.dn_mm, comprimento: t.comprimento_m, setor: t.setor,
    rugosidade: t.rugosidade }));
  const equipamentos: Equipamento[] = feicoes.map((f) => {
    const [lon, lat] = f.geometry.coordinates;
    const p = f.properties;
    const tipo = String(p.tipo) === "registro" && p.registro_fechado ? "registro_fechado" : String(p.tipo);
    return { tipo, id: String(p.id ?? ""), detalhe: String(p.nome ?? ""), lon, lat, setor: String(p.setor ?? ""), trecho: "",
      ativoId: String(p.ativo_id ?? ""), deviceId: (p.device_id as string | null) ?? null, situacao: String(p.situacao ?? ""),
      submersa: Boolean(p.submersa), poco: (p.poco as DadosPoco | null) ?? null,
      reservatorio: (p.reservatorio as Equipamento["reservatorio"]) ?? null, ...projetarLocal(lon, lat) };
  });
  // setor e trecho mais próximo (o setor do cadastro prevalece quando existe)
  const setoresCadastro = new Map(equipamentos.map((e) => [e, e.setor]));
  atribuirSetores(nos, trechos, equipamentos);
  equipamentos.forEach((e) => { const s = setoresCadastro.get(e); if (s) e.setor = s; });
  afastarEstacoes(equipamentos);
  const xs = [...nos.values()].map((n) => n.x), zs = [...nos.values()].map((n) => n.z);
  return { nos, trechos, equipamentos, origem, cotaMin: 0, cotaMax: 0,
    extensao: Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs)) };
}

/** Cota de cada nó pelo relevo (o cadastro de nós da importação não traz cota). */
export function aplicarCotas(rede: Rede, cota: (x: number, z: number) => number): void {
  rede.nos.forEach((n) => { n.cota = Math.round(cota(n.x, n.z) * 100) / 100; });
  const valores = [...rede.nos.values()].map((n) => n.cota);
  rede.cotaMin = Math.min(...valores);
  rede.cotaMax = Math.max(...valores);
}
